import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import DashboardLayout from '../components/DashboardLayout';
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface Category {
  _id?: string;
  name: string;
  nameArabic?: string;
  slug: string;
  type: 'main' | 'sub';
  active: boolean;
  order: number;
  description?: string;
  descriptionArabic?: string;
  icon?: string;
  parentCategory?: string;
}

export default function CategoryForm() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [, setMainCategories] = useState<Category[]>([]);
  const [formData, setFormData] = useState<Category>({
    name: '',
    nameArabic: '',
    slug: '',
    type: 'main',
    active: true,
    order: 0,
    description: '',
    descriptionArabic: '',
    icon: '',
    parentCategory: '',
  });

  useEffect(() => {
    loadMainCategories();
    if (isEditing) {
      loadCategory();
    }
  }, [id]);

  const loadMainCategories = async () => {
    try {
      const response = await api.get('/admin/categories', { params: { type: 'main', limit: 1000 } });
      setMainCategories(response.data.data || []);
    } catch (error) {
      console.error('Error loading main categories:', error);
    }
  };

  const loadCategory = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/categories/${id}`);
      const category = response.data.data;
      setFormData({
        name: category.name || '',
        nameArabic: category.nameArabic || '',
        slug: category.slug || '',
        type: category.type || 'main',
        active: category.active !== undefined ? category.active : true,
        order: category.order || 0,
        description: category.description || '',
        descriptionArabic: category.descriptionArabic || '',
        icon: category.icon || '',
        parentCategory: category.parentCategory?._id || category.parentCategory || '',
      });
    } catch (error) {
      console.error('Error loading category:', error);
      alert(t('categories.failedToLoad'));
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleNameChange = (name: string) => {
    setFormData({
      ...formData,
      name,
      slug: formData.slug || generateSlug(name),
    });
  };

  const uploadIcon = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'categories/icons');
    formData.append('type', 'image');
    
    setUploading(`Uploading ${file.name}...`);
    try {
      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploading(null);
      if (response.data.success && response.data.data) {
        return response.data.data.url;
      }
      throw new Error('Upload failed');
    } catch (error: any) {
      setUploading(null);
      console.error('Upload error:', error);
      throw new Error(error.response?.data?.message || t('categoryForm.failedToUploadIcon'));
    }
  };

  const handleIconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const url = await uploadIcon(file);
      setFormData({ ...formData, icon: url });
    } catch (error: any) {
      alert(error.message || t('categoryForm.failedToUploadIcon'));
    }
    e.target.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const submitData = {
        ...formData,
        slug: formData.slug || generateSlug(formData.name),
        type: 'main', // Categories form is for main categories only
        ...(formData.parentCategory && { parentCategory: formData.parentCategory }),
      };
      
      if (isEditing) {
        await api.put(`/admin/categories/${id}`, submitData);
      } else {
        await api.post('/admin/categories', submitData);
      }
      navigate('/');
    } catch (error: any) {
      console.error('Error saving category:', error);
      alert(error.response?.data?.message || t('categoryForm.failedToSave'));
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditing) {
    return (
      <DashboardLayout title={t('common.loading')}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-xl">{t('categoryForm.loading')}</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout 
      title={isEditing ? t('categoryForm.editCategory') : t('categoryForm.createCategory')}
      subtitle={t('categoryForm.subtitle')}
    >
      <div className="max-w-7xl mx-auto">
        {uploading && (
          <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="text-sm text-primary-orange">{uploading}</div>
          </div>
        )}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 space-y-6">
          {/* Basic Information */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">{t('categoryForm.basicInfo')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('categoryForm.nameRequired')}</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('categoryForm.nameAr')}</label>
                <input
                  type="text"
                  value={formData.nameArabic}
                  onChange={(e) => setFormData({ ...formData, nameArabic: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('categoryForm.slugRequired')}</label>
              <input
                type="text"
                required
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                placeholder={t('categoryForm.slugPlaceholder')}
              />
              <p className="text-xs text-gray-500 mt-1">{t('categoryForm.slugHint')}</p>
            </div>
          </section>

          {/* Icon */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">{t('categoryForm.iconSection')}</h2>
            <div className="flex items-center space-x-4">
              {formData.icon && (
                <div className="relative">
                  {formData.icon.startsWith('http') || formData.icon.startsWith('/') ? (
                    <img src={formData.icon} alt="Icon" className="w-20 h-20 object-cover rounded-lg" />
                  ) : (
                    <div className="w-20 h-20 flex items-center justify-center text-4xl bg-gray-100 rounded-lg">
                      {formData.icon}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, icon: '' })}
                    className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
              <div>
                <label className="inline-flex items-center px-4 py-2 bg-primary-orange text-white rounded-lg cursor-pointer hover:bg-orange-600">
                  <PhotoIcon className="w-5 h-5 mr-2" />
                  {t('categoryForm.uploadIconImage')}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleIconUpload}
                    className="hidden"
                  />
                </label>
                <p className="text-xs text-gray-500 mt-2">{t('categoryForm.orEmojiHint')}</p>
                <input
                  type="text"
                  value={formData.icon && !formData.icon.startsWith('http') && !formData.icon.startsWith('/') ? formData.icon : ''}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="mt-2 w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder={t('categoryForm.iconPlaceholder')}
                />
              </div>
            </div>
          </section>

          {/* Description */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">{t('categoryForm.description')}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('categoryForm.description')}</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('categoryForm.descriptionAr')}</label>
                <textarea
                  value={formData.descriptionArabic}
                  onChange={(e) => setFormData({ ...formData, descriptionArabic: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-orange focus:border-transparent"
                  rows={3}
                />
              </div>
            </div>
          </section>

          {/* Settings */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">{t('categoryForm.settingsSection')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('categoryForm.order')}</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
                <p className="text-xs text-gray-500 mt-1">{t('categoryForm.orderHint')}</p>
              </div>
              <div className="flex items-center">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">{t('categoryForm.active')}</span>
                </label>
              </div>
            </div>
          </section>

          {/* Form Actions */}
          <div className="flex flex-wrap justify-end gap-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-primary-orange hover:bg-orange-600 text-white rounded-lg disabled:opacity-50"
            >
              {loading ? t('categoryForm.saving') : isEditing ? t('categoryForm.updateCategory') : t('categoryForm.createCategoryBtn')}
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}

